import React, { useEffect, useState } from "react";
import { ThemeProvider } from "styled-components"; // Dodato ThemeProvider
import styled from "styled-components";
import {
  BoxContent,
  BoxWrrapper,
  MainWrapper,
  PageWrapper,
  RightContent,
  BoxTitle,
  FormWrap,
  CheckTheme,
  CheckThemeContent,
} from "../../style/DasboardMain";

// Import version from package.json
import packageJson from "../../../package.json";

// Components
import Navigation from "../../components/Navigation/Navigation";
import IntroText from "../../components/introText/index";
import RadioButton from "../../components/inputs/RadioButton";

// Language
import { useTranslation } from "react-i18next";
import { GlobalStyles, darkTheme, lightTheme } from "../../style/Global";
import { useLexicon } from "../../hooks/useLexicon";
import { LanguageSwitcher } from "../../components/LanguageSwitcher/LanguageSwitcher";
import { Link } from "react-router-dom";
import { getUserProfile } from "../../helpers/userProfile";
import {
  callAiMessages,
  callAiSpeech,
  callAiTranscribe,
} from "../../helpers/aiTestService";

// Styled component for version display
const VersionDisplay = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${props => props.theme.background || '#1a1a1a'};
  color: ${props => props.theme.white || '#ffffff'};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.7;
  z-index: 1000;
  border: 1px solid ${props => props.theme.borderColor || '#333'};
  
  &:hover {
    opacity: 1;
  }
`;

const AiPanelWrapper = styled.div`
  margin-top: 30px;
  margin-bottom: 60px;
  max-width: 1080px;
  border-radius: 5px;
  border: 1px solid ${(props) => props.theme.borderColor};
  box-shadow: 3px 2px 30px rgba(68, 68, 68, 0.12);
  background-color: ${(props) => props.theme.sidebar};
`;

const AiPanelContent = styled.div`
  padding: 24px;
  background: ${(props) => props.theme.black};
`;

const AiPanelTitle = styled.div`
  h2 {
    font-family: "SpaceGrotesk";
    font-size: 20px;
    line-height: 30px;
    color: ${(props) => props.theme.white};
    margin: 0 0 8px;
  }

  p {
    margin: 0 0 20px;
    color: ${(props) => props.theme.blueish};
    font-family: "SatoshiRegular";
    font-size: 14px;
    line-height: 20px;
  }
`;

const AiPanelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
  gap: 16px;
`;

const AiFormCard = styled.div`
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 5px;
  padding: 14px;
  background: ${(props) => props.theme.sidebar};
`;

const AiFormTitle = styled.h3`
  margin: 0 0 14px;
  font-family: "SpaceGrotesk";
  font-size: 16px;
  color: ${(props) => props.theme.white};
`;

const AiForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AiLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${(props) => props.theme.blueish};
  font-family: "SatoshiMedium";
  font-size: 13px;
`;

const AiInput = styled.input`
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 4px;
  padding: 8px 10px;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  font-size: 14px;
`;

const AiTextArea = styled.textarea`
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 4px;
  padding: 8px 10px;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  font-size: 14px;
  min-height: 74px;
  resize: vertical;
`;

const AiButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: 10px 12px;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 13px;
  text-transform: uppercase;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const AiResultMeta = styled.div`
  margin-top: 8px;
  color: ${(props) => props.theme.blueish};
  font-family: "SatoshiRegular";
  font-size: 12px;
`;

const AiResultOutput = styled.pre`
  margin: 8px 0 0;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 4px;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  padding: 10px;
  font-family: "SatoshiRegular";
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
  overflow: auto;
`;

const ExternalUploadLink = styled(Link)`
  display: inline-block;
  margin-top: 24px;
  color: ${(props) => props.theme.green};
  font-family: "SatoshiBold";
`;

const LOCAL_AI_PANEL_HOSTS = ["localhost", "127.0.0.1"];

const isLocalAiPanelEnabled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return LOCAL_AI_PANEL_HOSTS.includes(window.location.hostname);
};

const formatResponseOutput = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const getErrorDetails = async (error) => {
  const status = error?.response?.status ?? null;
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    const blobText = await responseData.text();
    return {
      status,
      output: formatResponseOutput(blobText) || error.message || "Request failed.",
    };
  }

  return {
    status,
    output:
      formatResponseOutput(responseData) ||
      error?.message ||
      "Request failed.",
  };
};

const Index = () => {
  const { t } = useTranslation();
  const l = useLexicon();

  const [selectedRadio, setSelectedRadio] = useState(localStorage.getItem("theme") || "dark");
  const [transcribeFile, setTranscribeFile] = useState(null);
  const [transcribeModel, setTranscribeModel] = useState("whisper-1");
  const [transcribePrompt, setTranscribePrompt] = useState("Transcribe this audio clip.");
  const [transcribeResult, setTranscribeResult] = useState({
    isLoading: false,
    status: null,
    output: "",
  });

  const [speechModel, setSpeechModel] = useState("tts-1");
  const [speechVoice, setSpeechVoice] = useState("nova");
  const [speechInput, setSpeechInput] = useState("I have added Saka.");
  const [speechResult, setSpeechResult] = useState({
    isLoading: false,
    status: null,
    output: "",
  });
  const [speechAudioUrl, setSpeechAudioUrl] = useState("");

  const [messagesModel, setMessagesModel] = useState("claude-haiku-4-5-20251001");
  const [messagesMaxTokens, setMessagesMaxTokens] = useState("256");
  const [messagesSystem, setMessagesSystem] = useState("You are a helpful football assistant.");
  const [messagesUserInput, setMessagesUserInput] = useState("Summarize this match in one sentence.");
  const [messagesResult, setMessagesResult] = useState({
    isLoading: false,
    status: null,
    output: "",
  });
  const [externalVideoUploadEnabled, setExternalVideoUploadEnabled] = useState(false);

  const showAiPanel = isLocalAiPanelEnabled();

  useEffect(() => {
    return () => {
      if (speechAudioUrl) {
        URL.revokeObjectURL(speechAudioUrl);
      }
    };
  }, [speechAudioUrl]);

  useEffect(() => {
    getUserProfile()
      .then((response) => setExternalVideoUploadEnabled(Boolean(response.data?.external_video_upload_enabled)))
      .catch(() => setExternalVideoUploadEnabled(false));
  }, []);

  const clearSpeechAudioUrl = () => {
    setSpeechAudioUrl((currentAudioUrl) => {
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }

      return "";
    });
  };

  const handleChange = (value) => {
    setSelectedRadio(value);
    localStorage.setItem("theme", value);
    window.location.reload();
  };

  const handleTranscribeSubmit = async (event) => {
    event.preventDefault();

    if (!transcribeFile) {
      setTranscribeResult({
        isLoading: false,
        status: null,
        output: "Select an audio file before calling /ai/transcribe.",
      });
      return;
    }

    setTranscribeResult({
      isLoading: true,
      status: null,
      output: "",
    });

    try {
      const response = await callAiTranscribe({
        file: transcribeFile,
        model: transcribeModel,
        prompt: transcribePrompt,
      });

      setTranscribeResult({
        isLoading: false,
        status: response.status,
        output: formatResponseOutput(response.data),
      });
    } catch (error) {
      const errorDetails = await getErrorDetails(error);
      setTranscribeResult({
        isLoading: false,
        status: errorDetails.status,
        output: errorDetails.output,
      });
    }
  };

  const handleSpeechSubmit = async (event) => {
    event.preventDefault();

    setSpeechResult({
      isLoading: true,
      status: null,
      output: "",
    });
    clearSpeechAudioUrl();

    try {
      const response = await callAiSpeech({
        model: speechModel,
        voice: speechVoice,
        input: speechInput,
      });

      const newAudioUrl = URL.createObjectURL(response.data);
      const contentType = response?.headers?.["content-type"] || "audio/mpeg";
      const output = `Received ${response.data?.size ?? 0} bytes (${contentType}).`;

      setSpeechResult({
        isLoading: false,
        status: response.status,
        output,
      });
      setSpeechAudioUrl(newAudioUrl);
    } catch (error) {
      const errorDetails = await getErrorDetails(error);
      setSpeechResult({
        isLoading: false,
        status: errorDetails.status,
        output: errorDetails.output,
      });
    }
  };

  const handleMessagesSubmit = async (event) => {
    event.preventDefault();

    setMessagesResult({
      isLoading: true,
      status: null,
      output: "",
    });

    const parsedMaxTokens = Number.parseInt(messagesMaxTokens, 10);

    try {
      const response = await callAiMessages({
        model: messagesModel,
        max_tokens: Number.isFinite(parsedMaxTokens) && parsedMaxTokens > 0 ? parsedMaxTokens : 1,
        system: messagesSystem,
        tools: [],
        messages: [
          {
            role: "user",
            content: messagesUserInput,
          },
        ],
      });

      setMessagesResult({
        isLoading: false,
        status: response.status,
        output: formatResponseOutput(response.data),
      });
    } catch (error) {
      const errorDetails = await getErrorDetails(error);
      setMessagesResult({
        isLoading: false,
        status: errorDetails.status,
        output: errorDetails.output,
      });
    }
  };

  return (
    <ThemeProvider theme={selectedRadio === "light" ? lightTheme : darkTheme}>
      <>
        <GlobalStyles />
        <MainWrapper>
          <Navigation />
          <RightContent>
            <div>
              <IntroText
                smallTitle={t("setup.smallTitle")}
                title={l(977)}
              />
              <LanguageSwitcher containerStyle={{top: 0, right: '5%', width: 'auto', left: 'auto'}} isTop={true} />
            </div>

            <PageWrapper>
              <BoxWrrapper>
                <BoxContent>
                  <BoxTitle>
                    <h2>{l(978)}</h2>
                    <p>{l(979)}</p>
                  </BoxTitle>
                  <FormWrap style={{ margin: 0 }}>
                    <CheckTheme>
                      <span>{l(980)}</span>
                      <CheckThemeContent>
                        <RadioButton
                          label={l(981)}
                          value="dark"
                          checked={selectedRadio === "dark"}
                          onChange={handleChange}
                        />
                        <RadioButton
                          label={l(982)}
                          value="light"
                          checked={selectedRadio === "light"}
                          onChange={handleChange}
                        />
                      </CheckThemeContent>
                    </CheckTheme>
                  </FormWrap>
                </BoxContent>
              </BoxWrrapper>
            </PageWrapper>

            {externalVideoUploadEnabled && (
              <ExternalUploadLink to="/external-video-upload">
                External Video Upload
              </ExternalUploadLink>
            )}

            {showAiPanel && (
              <AiPanelWrapper data-testid="ai-test-panel">
                <AiPanelContent>
                  <AiPanelTitle>
                    <h2>AI Route Test Panel</h2>
                    <p>Visible only on localhost / 127.0.0.1, uses your current auth token, and targets api-v1.smrtstats.com.</p>
                  </AiPanelTitle>

                  <AiPanelGrid>
                    <AiFormCard>
                      <AiFormTitle>POST /ai/transcribe</AiFormTitle>
                      <AiForm onSubmit={handleTranscribeSubmit}>
                        <AiLabel>
                          Audio file
                          <AiInput
                            type="file"
                            accept="audio/*"
                            onChange={(event) => {
                              const selectedFile = event.target.files?.[0] || null;
                              setTranscribeFile(selectedFile);
                            }}
                          />
                        </AiLabel>
                        <AiLabel>
                          model
                          <AiInput
                            type="text"
                            value={transcribeModel}
                            onChange={(event) => setTranscribeModel(event.target.value)}
                          />
                        </AiLabel>
                        <AiLabel>
                          prompt
                          <AiTextArea
                            value={transcribePrompt}
                            onChange={(event) => setTranscribePrompt(event.target.value)}
                          />
                        </AiLabel>
                        <AiButton type="submit" disabled={transcribeResult.isLoading}>
                          {transcribeResult.isLoading ? "Running..." : "Run /ai/transcribe"}
                        </AiButton>
                      </AiForm>
                      <AiResultMeta>HTTP status: {transcribeResult.status ?? "-"}</AiResultMeta>
                      {transcribeResult.output && (
                        <AiResultOutput>{transcribeResult.output}</AiResultOutput>
                      )}
                    </AiFormCard>

                    <AiFormCard>
                      <AiFormTitle>POST /ai/speech</AiFormTitle>
                      <AiForm onSubmit={handleSpeechSubmit}>
                        <AiLabel>
                          model
                          <AiInput
                            type="text"
                            value={speechModel}
                            onChange={(event) => setSpeechModel(event.target.value)}
                          />
                        </AiLabel>
                        <AiLabel>
                          voice
                          <AiInput
                            type="text"
                            value={speechVoice}
                            onChange={(event) => setSpeechVoice(event.target.value)}
                          />
                        </AiLabel>
                        <AiLabel>
                          input
                          <AiTextArea
                            value={speechInput}
                            onChange={(event) => setSpeechInput(event.target.value)}
                          />
                        </AiLabel>
                        <AiButton type="submit" disabled={speechResult.isLoading}>
                          {speechResult.isLoading ? "Running..." : "Run /ai/speech"}
                        </AiButton>
                      </AiForm>
                      <AiResultMeta>HTTP status: {speechResult.status ?? "-"}</AiResultMeta>
                      {speechResult.output && (
                        <AiResultOutput>{speechResult.output}</AiResultOutput>
                      )}
                      {speechAudioUrl && (
                        <div style={{ marginTop: "10px" }}>
                          <audio controls src={speechAudioUrl}>
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}
                    </AiFormCard>

                    <AiFormCard>
                      <AiFormTitle>POST /ai/messages</AiFormTitle>
                      <AiForm onSubmit={handleMessagesSubmit}>
                        <AiLabel>
                          model
                          <AiInput
                            type="text"
                            value={messagesModel}
                            onChange={(event) => setMessagesModel(event.target.value)}
                          />
                        </AiLabel>
                        <AiLabel>
                          max_tokens
                          <AiInput
                            type="number"
                            min="1"
                            value={messagesMaxTokens}
                            onChange={(event) => setMessagesMaxTokens(event.target.value)}
                          />
                        </AiLabel>
                        <AiLabel>
                          system
                          <AiTextArea
                            value={messagesSystem}
                            onChange={(event) => setMessagesSystem(event.target.value)}
                          />
                        </AiLabel>
                        <AiLabel>
                          user message
                          <AiTextArea
                            value={messagesUserInput}
                            onChange={(event) => setMessagesUserInput(event.target.value)}
                          />
                        </AiLabel>
                        <AiButton type="submit" disabled={messagesResult.isLoading}>
                          {messagesResult.isLoading ? "Running..." : "Run /ai/messages"}
                        </AiButton>
                      </AiForm>
                      <AiResultMeta>HTTP status: {messagesResult.status ?? "-"}</AiResultMeta>
                      {messagesResult.output && (
                        <AiResultOutput>{messagesResult.output}</AiResultOutput>
                      )}
                    </AiFormCard>
                  </AiPanelGrid>
                </AiPanelContent>
              </AiPanelWrapper>
            )}
          </RightContent>
        </MainWrapper>
        
        {/* Version display in bottom right corner */}
        <VersionDisplay>
          v{packageJson.version}
        </VersionDisplay>

      </>
    </ThemeProvider>
  );
};

export default Index;
